import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "../../../generated/app-service-gql";
import {
  TeamManagementUsersQuery,
  UpdateUsersMutation,
  UpdateUsersMutationVariables,
} from "../../../generated/app-service-gql/graphql";
import { useGraphQlMutation } from "../useGraphQlClient";

const UPDATE_USERS_MUTATION = graphql(`
  mutation updateUsers($request: UpdateUsersRequest!) {
    updateUsers(users: $request) {
      users {
        guid
        firstName
        lastName
        mainGoal
        teamId
        isCompanyAdmin
      }
    }
  }
`);

/**
 * API which allows company admins to update details about users in their
 * company. For example it can be used for updating teams and company admin
 * status.
 *
 * @param options
 * @returns
 */
export function useUpdateUsers(
  options?: UseMutationOptions<UpdateUsersMutation, unknown, UpdateUsersMutationVariables, unknown>,
) {
  const queryClient = useQueryClient();
  return useGraphQlMutation(UPDATE_USERS_MUTATION, {
    ...options,
    onSuccess: async (data, variables, context) => {
      const updatedUsers = data.updateUsers.users;

      // Find all matching queries (not exact)
      const queryCache = queryClient.getQueryCache();

      const teamManagementQueries = queryCache.findAll({
        queryKey: ["team_management_users"],
        exact: false,
      });

      for (const query of teamManagementQueries) {
        queryClient.setQueryData<TeamManagementUsersQuery>(query.queryKey, (prevData) => {
          if (!prevData) return prevData;

          const page = prevData.teamManagementUsers;
          return {
            teamManagementUsers: {
              ...page,
              results: page.results.map((user) => {
                const userThatWasUpdated = updatedUsers.find((u) => u.guid === user.guid);
                if (!userThatWasUpdated) return user;
                return {
                  ...user,
                  team: {
                    ...user.team,
                    id: userThatWasUpdated.teamId,
                  },
                  companyAdmin: !!userThatWasUpdated.isCompanyAdmin,
                };
              }),
            },
          };
        });
      }

      // A team or admin change can reorder or refilter any cached page, so each refetches when next
      // shown. The caller refetches the page on screen itself, after resetting its edit state.
      await queryClient.invalidateQueries({
        queryKey: ["team_management_users"],
        exact: false,
        refetchType: "none",
      });
      await queryClient.invalidateQueries({ queryKey: ["users"], exact: false });

      for (const user of data.updateUsers.users) {
        await queryClient.invalidateQueries({ queryKey: ["user", user.guid] });
      }

      options?.onSuccess?.(data, variables, context);
    },
  });
}
